import { useEffect, useState } from 'react';
import { Alert, AlertColor, Box, Card, TextField, CardContent, CircularProgress, FormControl, Grid, InputLabel, ListItemText, MenuItem, OutlinedInput, Paper, Select, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';

import { useVForm } from 'shared/forms';

import { FerramentasDeDetalhe, InputError, ConfirmacaoModal } from 'shared/components';
import { LayoutBaseDePagina } from 'shared/layouts';
import { t } from 'i18next';
import { ICreateUsuarioOrg, InicitalCreateUsuarioOrg } from 'shared/types';
import { useHasPermissionAcao } from 'shared/hooks';
import { IKeyValue, listagemOrgToIKeyValue } from 'shared/utils';
import { UsuariosService } from 'shared/services/usuarios/usuariosService';
import { OrganizationsService } from 'shared/services/organizations/organizationsService';


const validationSchema = yup.object({
  CodigoOrganizacao: yup.number().required().moreThan(0)
});

export const DetalheDeUsuarioOrg: React.FC = () => {
  const { idUsuario } = useParams<'idUsuario'>();
  const { id = 'novo' } = useParams<'id'>();
  const navigate = useNavigate();

  const hasPermitionCreate = useHasPermissionAcao('usuario', 'CRE');
  const hasPermitionDelete = useHasPermissionAcao('usuario', 'DEL');
  const hasPermitionUpdate = false;


  const [alert, setAlert] = useState<null | { text: string; type: AlertColor }>(null);
  const { formRef, saveAndClose, isSaveAndClose } = useVForm();

  const { register, handleSubmit, reset, watch, formState: { errors }, } = useForm({
    resolver: yupResolver(validationSchema),
    reValidateMode: 'onBlur'
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingRole, setIsLoadingRole] = useState(false);
  const [openConfirmacao, setOpenConfirmacao] = useState(false);
  const [titleConfirmacao, setTitleConfirmacao] = useState('');
  const [messageConfirmacao, setMessageConfirmacao] = useState('');

  const selectOrg = watch('CodigoOrganizacao');


  const [listKeyValueOrg, setListKeyValueOrg] = useState<IKeyValue[]>([]);



  const [stateSnack, setStateSnack] = useState({
    open: false,
    severity: 'success',
    message: '',
  });

  useEffect(() => {
    setIsLoading(true);

    OrganizationsService.getAll()
      .then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          setStateSnack({ open: true, severity: 'error', message: `Ocorreu o seguinte erro: ${result.message}` });
        } else {
          setListKeyValueOrg(listagemOrgToIKeyValue(result.rows));
        }
      });
  }, []);


  useEffect(() => {
    setAlert(null);
    if (id !== 'novo') {
      setIsLoading(true);

      UsuariosService.getUsuarioOrg(Number(idUsuario), Number(id))
        .then((result) => {
          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
            navigate(`/usuario/detalhe/${idUsuario}`);
          } else {
            delete result.ROW_NUM;
            reset(result);
          }
          setIsLoading(false);
        });
    }
    else {
      reset(InicitalCreateUsuarioOrg);
    }

  }, [id]);



  const confirmDelete = () => {
    setAlert(null);
    UsuariosService.deleteUsuarioRole(Number(idUsuario), Number(id))
      .then(result => {
        if (result instanceof Error) {
          setAlert({
            text: result.message,
            type: 'error',
          });
          setOpenConfirmacao(false);
        } else {
          setAlert({
            text: 'Registro apagado com sucesso!',
            type: 'success',
          });
          navigate(`/usuario/detalhe/${idUsuario}`);
        }
      })
      .catch(reason => {
        setAlert({
          text: reason,
          type: 'error',
        });
        setOpenConfirmacao(false);
      });
  };

  const onSubmit = async (data: ICreateUsuarioOrg) => {
    setAlert(null);
    setIsSubmitting(true);
    if (id === 'novo') {
      UsuariosService
        .createUsuarioOrg(Number(idUsuario),data)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setAlert({
              text: result.message,
              type: 'error',
            });
          } else {
            console.log('isSaveAndClose', isSaveAndClose());
            if (isSaveAndClose()) {
              navigate(`/usuario/detalhe/${idUsuario}/org`);
            } else {
              navigate(`/usuario/detalhe/${idUsuario}/org/${data.CodigoOrganizacao}`);
            }
          }
        })
        .finally(() => {
          setIsSubmitting(false);
        });

    }
  };



  return (
    <LayoutBaseDePagina
      titulo={id === 'novo' ? t('pages.usuarioOrg.newUsuOrg') : hasPermitionUpdate ? 'Alterar Usuário x Org' : 'Consultar Usuário x Org'}
      barraDeFerramentas={!(isLoading || isSubmitting || isLoadingRole) && (
        <FerramentasDeDetalhe
          idForm='formDetalheDeUsuarioOrg'
          mostrarBotaoSalvarEFechar={hasPermitionCreate && id == 'novo'}
          mostrarBotaoSalvar={false}
          mostrarBotaoNovo={false}
          mostrarBotaoApagar={false}
          aoClicarEmVoltar={() => navigate(`/usuario/detalhe/${idUsuario}/org`)}
          aoClicarEmSalvarEFechar={saveAndClose}

        />)
      }
    >
      <Box ref={formRef} id='formDetalheDeUsuarioOrg' component="form" display="flex" autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined" width='100%' >

          <Card >
            <CardContent sx={{ padding: 0 }} >
              <div>
                {alert ? (
                  <>
                    <Grid item xs={12} paddingX={2}>
                      <Alert severity={alert.type} onClose={() => setAlert(null)} sx={{ marginTop: '1rem', marginBottom: '0' }}>
                        {alert.text}
                      </Alert>
                    </Grid>
                  </>
                ) : (
                  <Grid item xs={12} component={Typography} paddingX={2}>

                  </Grid>
                )}
                <Grid container  >
                  

                  <Grid item xs={4} paddingX={2}>
                    <FormControl fullWidth sx={{ marginTop: '1rem', marginBottom: '0' }}>
                      <InputLabel id='label-select-tipo'>{`${t('pages.orgFornecedora.Organizacao')} *`}</InputLabel>
                      <Select
                        disabled={isSubmitting || isLoading || id != 'novo'}
                        error={errors?.CodigoOrganizacao?.type ? true : false}
                        labelId='label-select-tipo'
                        id='CodigoOrganizacao'

                        input={<OutlinedInput label={`${t('pages.organizationSeletion.organization')} *`} />}
                        {...register('CodigoOrganizacao')}
                        value={selectOrg || ''}

                      >
                        <MenuItem value=''>
                          <ListItemText primary='' />
                        </MenuItem>
                        {listKeyValueOrg.map((item) => (
                          <MenuItem key={item.key} value={item.key} >
                            <ListItemText primary={item.value} sx={{ margin: '0' }} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {errors?.CodigoOrganizacao?.type && <InputError type={errors.CodigoOrganizacao.type} field='CodigoOrganizacao' />}

                  </Grid>

                </Grid>
              </div>
            </CardContent>
          </Card>
        </Box>
        {<ConfirmacaoModal
          open={openConfirmacao}
          title={titleConfirmacao}
          message={messageConfirmacao}
          onClose={() => setOpenConfirmacao(false)}
          onConfirm={() => confirmDelete()}
        />}
      </Box>
      {(isLoading || isSubmitting || isLoadingRole) && (<Box sx={{
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        position: 'absolute',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <CircularProgress />
      </Box>)}

    </LayoutBaseDePagina >
  );
};
